// components/ChatBot.tsx
"use client";

import { useEffect } from "react";

declare global {
  interface Window {
    Chatbot: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      init: (config: any) => void;
      destroy: () => void;
    };
  }
}

interface ChatBotProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: any;
  containerStyle?: React.CSSProperties;
}

const defaultConfig = {
  chatflowid:
    process.env.NEXT_PUBLIC_CHATFLOW_ID ||
    "68aedf24-14fb-429e-b487-4b4c90b5690c",
  apiHost:
    process.env.NEXT_PUBLIC_FLOWISE_HOST || "https://cloud.flowiseai.com",
  chatflowConfig: {
    temperature: 0.7,
    maxTokens: 500,
    pineconeNamespace: "default",
  },
  observersConfig: {
    showTypingIndicator: true,
    showSourceDocs: true,
    showDateTime: true,
    showFeedback: true,
    showDisclaimer: true,
    showAutoOpen: true,
  },
  theme: {
    button: {
      backgroundColor: "#66d1d1",
      right: 20,
      bottom: 20,
      size: 48,
      dragAndDrop: true,
      iconColor: "white",
      autoWindowOpen: {
        autoOpen: true,
        openDelay: 2,
        autoOpenOnMobile: false,
      },
    },
    tooltip: {
      showTooltip: true,
      tooltipMessage: "Hi! I’m Lynx!",
      tooltipBackgroundColor: "#001f3f",
      tooltipTextColor: "white",
      tooltipFontSize: 16,
    },
    // disclaimer: {
    //   title: "Disclaimer",
    //   message:
    //     'By using this chatbot, you agree to the <a target="_blank" href="https://www.linxarlabs.com/privacy_policy">Privacy Policy</a>',
    //   textColor: "black",
    //   buttonColor: "#66d1d1",
    //   buttonText: "Start Chatting",
    //   buttonTextColor: "white",
    //   blurredBackgroundColor: "#99a5b2",
    // },
    chatWindow: {
      showTitle: true,
      showAgentMessages: true,
      title: "LynX",
      subtitle: "LinXar Labs",
      titleAvatarSrc: "/lynxAvatar.gif",
      welcomeMessage:
        "Lynx is here to help with any question about LinXar Labs -- This is a beta version.",
      errorMessage:
        "Oops! It seems I’ve run into a snag 🛠️. Don’t worry—let’s try that again or rephrase your query. Lynx is here to help!",
      backgroundColor: "#ffffff",
      height: 600,
      width: 400,
      fontSize: 14,
      starterPrompts: [
        "Can you help me automate part of my business workflow?",
        "How does LinXar Labs approach a new web or app development project?",
        "What’s included in your automation audit?",
        "Show me examples of industries LinXar Labs serves.",
        "Can you help my company integrate AI into our existing systems?",
        "I want to build a SaaS platform — where should I start?",
        "What makes LinXar Labs different from other tech agencies?",
        "Can you explain how automation can reduce my operational costs?",
        "Tell me about your Fintech and enterprise automation solutions.",
        "How can I book a free consultation with LinXar Labs?",
        "Do you provide long-term technical support and maintenance?",
        "What kind of clients or businesses do you typically work with?",
        "What technologies does LinXar Labs specialize in?",
        "I’m interested in custom CRM or internal tools — can you build those?",
        "How can automation help me scale my startup faster?",
      ],
      starterPromptFontSize: 15,
      clearChatOnReload: false,
      sourceDocsTitle: "Sources:",
      renderHTML: true,
      botMessage: {
        backgroundColor: "#f7f8ff",
        textColor: "#303235",
        showAvatar: true,
        avatarSrc: "/lynxAvatar.gif",
      },
      userMessage: {
        backgroundColor: "#001f3f",
        textColor: "#ffffff",
        showAvatar: true,
        avatarSrc:
          "https://raw.githubusercontent.com/zahidkhawaja/langchain-chat-nextjs/main/public/usericon.png",
      },
      textInput: {
        placeholder: "Type your question",
        backgroundColor: "#ffffff",
        textColor: "#303235",
        sendButtonColor: "#66d1d1",
        maxChars: 50,
        maxCharsWarningMessage:
          "You exceeded the characters limit. Please input less than 50 characters.",
        autoFocus: true,
        sendMessageSound: true,
        receiveMessageSound: true,
      },
      feedback: {
        color: "#303235",
      },
      dateTimeToggle: {
        date: true,
        time: true,
      },
      footer: {
        textColor: "#303235",
        text: "Innovating Tomorrow, Today!",
        company: "LinXar Labs",
        companyLink: "https://www.linxarlabs.com",
      },
    },
  },
};

const defaultContainerStyle: React.CSSProperties = {
  position: "fixed",
  bottom: "20px",
  right: "20px",
  zIndex: 1000,
};

export default function ChatBot({
  config = {},
  containerStyle = defaultContainerStyle,
}: ChatBotProps) {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/flowise-embed/dist/web.js";
    script.type = "module";
    script.async = true;

    script.onload = () => {
      if (window.Chatbot) {
        window.Chatbot.init({
          ...defaultConfig,
          ...config,
          theme: {
            ...defaultConfig.theme,
            ...config.theme,
          },
        });
      }
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      if (window.Chatbot) {
        window.Chatbot.destroy();
      }
    };
  }, [config]);

  return <div id="flowise-chatbot-container" style={containerStyle} />;
}
